/**
 * §P.6 fase 2 · El horario de silencio.
 *
 * ## Protege la vida personal del maestro, y eso es media promesa comercial
 *
 * Así lo dice el contrato, y por eso esta pantalla no es una preferencia más: es una
 * de las cosas que el producto promete a quien lo compra. Lo respetan **todos** los
 * envíos salvo tres —`session.cancelled`, `checkin.*` y `support.session_started`—, y
 * «no hay más excepciones y no se añaden por urgencia percibida». Las tres se nombran
 * en pantalla: un maestro que reciba un aviso a las once tiene que poder saber por
 * qué. La tercera faltaba aquí hasta el 13-10-2026, aunque el servidor ya la hacía.
 *
 * ## No cancela avisos: los retrasa
 *
 * «La fila se escribe igual con `sent_at` a nulo». Hay que decirlo, porque un maestro
 * que crea que el aviso se perdió lo manda otra vez por WhatsApp — y entonces la
 * familia recibe dos.
 *
 * ## La ventana **cruza la medianoche**, y ese es el caso normal
 *
 * 21:30 → 08:00. La comprobación de «¿estoy en silencio ahora?» se hace con eso en
 * cuenta (ver `enSilencio`), porque decir «no» a las once de la noche destruiría la
 * promesa en la primera pantalla donde se mira.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import {
  duracionDelSilencio,
  duracionEnPalabras,
  enSilencio,
  horaEnZona,
  type HorarioDeSilencio as Horario,
} from '@/club/avisos/comunicacion';

/** Más de doce horas de silencio casi siempre es el `from` y el `to` al revés. */
const SOSPECHOSO_DESDE = 12 * 60;

export function HorarioDeSilencio({
  club,
  puedeEditar,
}: {
  club: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [sembradoDe, setSembradoDe] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [guardado, setGuardado] = useState(false);

  const horario = useQuery({
    queryKey: ['horario-de-silencio', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })['quiet-hours'].get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Horario;
    },
  });

  if (horario.data && sembradoDe !== club) {
    setSembradoDe(club);
    setDesde(horario.data.from);
    setHasta(horario.data.to);
  }

  const guardar = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        ['quiet-hours'].put({ from: desde, to: hasta });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setGuardado(true);
      void cache.invalidateQueries({ queryKey: ['horario-de-silencio', club] });
    },
    onError: (e) => {
      setGuardado(false);
      setProblema(aProblema(e));
    },
  });

  if (horario.isPending) {
    return <Cargando filas={4} etiqueta="Cargando el horario" />;
  }

  if (horario.isError || !horario.data) {
    return (
      <PanelError
        problema={aProblema(horario.error)}
        reintentar={() => void horario.refetch()}
      />
    );
  }

  const h = horario.data;
  const ahora = horaEnZona(h.timezone);
  /* Con el horario **guardado**, no con el que se está escribiendo. */
  const callado = enSilencio(h, ahora);
  const minutos = duracionDelSilencio(desde, hasta);
  const cambiado = desde !== h.from || hasta !== h.to;

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        guardar.mutate();
      }}
    >
      <h1>Horario de silencio</h1>

      <p className="tenue">
        Las horas en las que Kiap no interrumpe a nadie. Está para{' '}
        <strong>proteger tu vida personal</strong>, no la de las familias: sin
        esto, un club acaba contestando mensajes a las once de la noche porque
        la aplicación se lo pone fácil.
      </p>

      {/*
        Si ahora mismo hay silencio o no. Es lo que se viene a comprobar, y responde a
        la pregunta que trae a esta pantalla: «¿por qué no ha salido el aviso?».
      */}
      <div className="aviso aviso-calma" role="status">
        {callado ? (
          <>
            <strong>Ahora mismo estás en silencio.</strong> Son las {ahora} en{' '}
            {h.timezone}, y no sale nada hasta las {h.to}.
          </>
        ) : (
          <>
            <strong>Ahora mismo no hay silencio.</strong> Son las {ahora} en{' '}
            {h.timezone}; empieza a las {h.from}.
          </>
        )}
      </div>

      {h.is_default && (
        <p className="tenue">
          Todavía no lo has tocado: estas son las horas de fábrica.
        </p>
      )}

      <div className="rejilla">
        <label>
          Empieza
          <input
            required
            type="time"
            value={desde}
            onChange={(e) => {
              setDesde(e.target.value);
              setGuardado(false);
            }}
          />
        </label>

        <label>
          Termina
          <input
            required
            type="time"
            value={hasta}
            onChange={(e) => {
              setHasta(e.target.value);
              setGuardado(false);
            }}
          />
        </label>
      </div>

      <p className="tenue">
        {desde === hasta
          ? 'Con la misma hora de inicio y de fin no hay ventana de silencio.'
          : `Son ${duracionEnPalabras(minutos)} de silencio al día.`}
      </p>

      {/*
        El error que no se ve: poner 08:00 → 21:30 pensando en las horas de trabajo, y
        acabar con trece horas de silencio en mitad del día. La duración lo destapa,
        pero conviene decirlo con todas las letras.
      */}
      {minutos > SOSPECHOSO_DESDE && desde !== hasta && (
        <div className="aviso" role="status">
          <strong>Eso es mucho silencio.</strong> Las horas se escriben al revés
          de como se piensan: <strong>empieza</strong> es cuando Kiap se calla
          —al final del día— y <strong>termina</strong> es cuando vuelve a
          hablar por la mañana. Lo normal es algo como 21:30 → 08:00.
        </div>
      )}

      <h2 className="seccion">Las tres excepciones</h2>

      <p className="tenue">
        Tres cosas salen igual aunque sea de noche, y no se pueden apagar:{' '}
        <strong>una clase cancelada</strong> —si mañana no hay entrenamiento, la
        familia tiene que saberlo esta noche—,{' '}
        <strong>las entradas y salidas de clase</strong>, que es lo que un padre
        mira cuando su hijo debería haber llegado ya, y el aviso de que{' '}
        <strong>alguien de Kiap ha entrado en tu club en modo soporte</strong>,
        que te llega a ti. No hay más, y no se añaden por urgencia percibida.
      </p>

      <p className="tenue">
        Y una cosa importante: el silencio{' '}
        <strong>no cancela los avisos, los retrasa</strong>. Lo que se manda a
        las once sale a las {h.to || '08:00'}, así que no hace falta repetirlo
        por otro lado.
      </p>

      {problema && <PanelError problema={problema} />}

      {guardado && !guardar.isPending && (
        <div className="aviso aviso-calma" role="status">
          <strong>Guardado.</strong>
        </div>
      )}

      {puedeEditar && (
        <div className="acciones">
          <button type="submit" disabled={guardar.isPending || !cambiado}>
            {guardar.isPending ? 'Guardando…' : 'Guardar el horario'}
          </button>
          {cambiado && (
            <button
              type="button"
              className="btn-plano"
              disabled={guardar.isPending}
              onClick={() => {
                setDesde(h.from);
                setHasta(h.to);
                setGuardado(false);
              }}
            >
              Deshacer
            </button>
          )}
        </div>
      )}
    </form>
  );
}
